import { fromEvent, Observable, Subject } from 'rxjs';
import { concatMap, debounceTime, map, take } from 'rxjs/operators';

type OverflowDir = 'X' | 'Y';

function isScrollDisabledByCss(element: Element, dir: OverflowDir = 'Y') {
    if (!(element instanceof Element)) {
        return false;
    }
    const overflow = getComputedStyle(element)[`overflow${dir}`] || '';
    return overflow.includes('hidden') || overflow.includes('visible');
}

function isScrollable(element: Element, dir: OverflowDir = 'Y'): boolean {
    const alreadyScrolled = element[dir === 'Y' ? 'scrollTop' : 'scrollLeft'] > 0;
    const contentOverflows = element[dir === 'Y' ? 'scrollHeight' : 'scrollWidth'] > element[dir === 'Y' ? 'clientHeight' : 'clientWidth'];
    return (alreadyScrolled || contentOverflows) && !isScrollDisabledByCss(element, dir);
}

function getScrollParent(element: Element, dir: OverflowDir = 'Y'): Element | null {
    if (!element) {
        return;
    }

    if (isScrollable(element, dir)) {
        return element;
    }

    return getScrollParent(element.parentElement);
}

const queuedScrollAction$ = new Subject<() => Observable<unknown>>();
queuedScrollAction$.asObservable().pipe(
    concatMap(fn => fn()),
).subscribe();

export function queueScrollWith(el: Element, action: () => void): Promise<EventTarget> {
    const parentElement = getScrollParent(el, 'Y') || getScrollParent(el, 'X') || window;
    const scroll$ = fromEvent(parentElement, 'scroll').pipe(
        map(e => e.target),
        debounceTime(100),
        take(1),
    );
    queuedScrollAction$.next(() => {
        action();
        return scroll$;
    });
    return scroll$.toPromise();
}

